/**
 * ProductForm Component - Story 6.3: Create Add Product Form
 * Main form controller component for multi-step product creation
 * Simplified to 3 steps: Product Details, Pricing, Images
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProductForm } from '../../hooks/useProductForm'
import { useImageUpload } from '../../hooks/useImageUpload'
import { FormStepper } from './FormStepper'
import { ProductDetailsStep } from './steps/ProductDetailsStep'
import { PricingStep } from './steps/PricingStep'
import { ImagesStep } from './steps/ImagesStep'
import { FormNavigation } from './FormNavigation'
import { FORM_STEPS, PRODUCT_CATEGORIES } from '../../constants/productFormConstants'
import { Button } from '@/components/ui/button'
import { Loader2, Check } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import './ProductForm.css'

export interface ProductFormProps {
  onSuccess?: (productId: string) => void;
  mode?: 'create' | 'edit';
  productId?: string;
  initialData?: {
    name: string;
    category: string;
    description: string;
    price: number;
    unit: string;
    quantity: number;
    minimumOrderQuantity?: number;
    state: string;
    district: string;
    pincode: string;
    address?: string;
    images?: Array<{
      imageId: string;
      url: string;
    }>;
  };
}

/**
 * ProductForm Component
 * Multi-step form for creating or editing product listings
 *
 * @component
 * @example
 * // Create mode
 * <ProductForm mode="create" onSuccess={(id) => console.log('Created:', id)} />
 *
 * // Edit mode
 * <ProductForm
 *   mode="edit"
 *   productId="prod-123"
 *   initialData={productData}
 *   onSuccess={(id) => console.log('Updated:', id)}
 * />
 */
export function ProductForm({
  onSuccess,
  mode = 'create',
  productId,
  initialData,
}: ProductFormProps) {
  const navigate = useNavigate()
  const [showExitDialog, setShowExitDialog] = useState(false)

  // Initialize hooks
  const productForm = useProductForm({
    mode,
    productId,
    initialData,
    onSuccess: (updatedProductId) => {
      onSuccess?.(updatedProductId)
      // Delay navigation to allow page to update with new data
      // In edit mode, the My Products list will be refreshed when we navigate back
      setTimeout(() => {
        const currentPath = window.location.pathname;
        if (currentPath.includes('/sales')) {
          navigate('/sales/my-products');
        } else {
          navigate('/marketplace/seller/products');
        }
      }, 2000)
    },
  })

  const imageUpload = useImageUpload(undefined, undefined, mode === 'edit' ? productId : undefined)

  // Pre-populate images from initialData if in edit mode
  // Re-run every time initialData changes (e.g., when editing different products or after page refresh)
  // User can remove images they don't want, add new ones, or keep existing ones
  // Only the final selected images will be saved
  useEffect(() => {
    if (mode === 'edit' && initialData?.images) {
      console.log('[ProductForm] Re-initializing images from initialData:', {
        productId,
        imageCount: initialData.images.length,
      });

      imageUpload.setImages(
        initialData.images.map((img) => {
          // Image URLs like /uploads/... are served via Vite proxy (dev) or gateway (prod)
          return {
            file: null,  // null for existing images
            preview: img.url,
            id: img.imageId,  // ID marks this as existing image
          };
        })
      )
    }
  }, [mode, productId, initialData?.images, initialData?.images?.length])

  // Sync images from imageUpload hook to productForm
  useEffect(() => {
    productForm.setFormData({ images: imageUpload.images })
  }, [imageUpload.images])

  // Handle step changes
  const handleNextStep = async () => {
    await productForm.goToNextStep()
  }

  const handlePreviousStep = () => {
    productForm.goToPreviousStep()
  }

  const handleSubmit = async () => {
    await productForm.submitForm()
  }

  const handleCancel = () => {
    if (productForm.formData.name || imageUpload.images.length > 0) {
      setShowExitDialog(true)
    } else {
      // Use current route context for navigation
      const currentPath = window.location.pathname;
      if (currentPath.includes('/sales')) {
        navigate('/sales/my-products');
      } else {
        navigate('/marketplace/seller/products');
      }
    }
  }

  const handleConfirmExit = () => {
    setShowExitDialog(false)
    productForm.resetForm()
    imageUpload.clearAllImages()
    // Use current route context for navigation
    const currentPath = window.location.pathname;
    if (currentPath.includes('/sales')) {
      navigate('/sales/my-products');
    } else {
      navigate('/marketplace/seller/products');
    }
  }

  // Get category label from category value
  const getCategoryLabel = (categoryValue: string) => {
    const category = PRODUCT_CATEGORIES.find(c => c.value === categoryValue)
    return category?.label || categoryValue
  }

  // Render step components based on current step
  const renderStep = () => {
    switch (productForm.currentStep) {
      case 1:
        return (
          <ProductDetailsStep
            formData={{
              name: productForm.formData.name,
              category: productForm.formData.category,
              description: productForm.formData.description,
            }}
            errors={productForm.errors}
            onChange={(data) => productForm.setFormData(data)}
            isValidating={productForm.isValidating}
          />
        )

      case 2:
        return (
          <PricingStep
            formData={{
              price: productForm.formData.price,
              unit: productForm.formData.unit,
              quantity: productForm.formData.quantity,
              minimumOrderQuantity: productForm.formData.minimumOrderQuantity,
            }}
            errors={productForm.errors}
            onChange={(data) => productForm.setFormData(data)}
            isValidating={productForm.isValidating}
          />
        )

      case 3:
        return (
          <div className="images-step-with-submit">
            <ImagesStep
              images={imageUpload.images}
              onAddImages={imageUpload.addImages}
              onRemoveImage={imageUpload.removeImage}
              isLoading={imageUpload.isUploading}
              errors={productForm.errors}
              mode={mode}
            />

            {/* Minimal Submit Section */}
            <div className="submit-summary-section">
              <h3 className="submit-summary-title">Submit Product</h3>

              <div className="submit-summary-content">
                <div className="summary-item">
                  <span className="summary-label">Product:</span>
                  <span className="summary-value">{productForm.formData.name || 'Not set'}</span>
                </div>

                <div className="summary-item">
                  <span className="summary-label">Category:</span>
                  <span className="summary-value">
                    {productForm.formData.category ? getCategoryLabel(productForm.formData.category) : 'Not set'}
                  </span>
                </div>

                <div className="summary-item">
                  <span className="summary-label">Price:</span>
                  <span className="summary-value">
                    {productForm.formData.price ? `₹${productForm.formData.price} / ${productForm.formData.unit}` : 'Not set'}
                  </span>
                </div>

                <div className="summary-item">
                  <span className="summary-label">Images:</span>
                  <span className="summary-value">{imageUpload.images.length} uploaded</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleSubmit}
                disabled={productForm.isSubmitting || productForm.isValidating}
                className="submit-product-button"
                aria-label="Submit product"
              >
                {productForm.isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin mr-2" aria-hidden="true" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check size={18} className="mr-2" aria-hidden="true" />
                    Submit Product
                  </>
                )}
              </Button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="product-form-container">
      <div className="form-wrapper">
        {/* Form Header */}
        <div className="form-header">
          <h1 className="form-title">
            {mode === 'edit' ? 'Edit Product' : 'Add New Product'}
          </h1>
          <p className="form-description">
            {mode === 'edit'
              ? 'Update your product details'
              : 'Complete all steps to create your product listing'}
          </p>
        </div>

        {/* Stepper */}
        <FormStepper
          steps={FORM_STEPS}
          currentStep={productForm.currentStep}
          totalSteps={productForm.totalSteps}
        />

        {/* Form Content */}
        <div className="form-content">
          {productForm.errors['general'] && (
            <div className="form-error-message" role="alert">
              {productForm.errors['general']}
            </div>
          )}

          {renderStep()}
        </div>

        {/* Form Navigation */}
        <FormNavigation
          currentStep={productForm.currentStep}
          totalSteps={productForm.totalSteps}
          onNext={handleNextStep}
          onPrevious={handlePreviousStep}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={productForm.isSubmitting || productForm.isValidating}
          isSubmitting={productForm.isSubmitting}
        />
      </div>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogTitle>Exit without saving?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. Are you sure you want to leave without
            saving?
          </AlertDialogDescription>
          <div className="flex gap-3 justify-end">
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmExit}
              className="bg-red-600 hover:bg-red-700"
            >
              Discard Changes
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
